import { BroadcastChannel } from 'broadcast-channel';
import { useEffect } from 'react';

export type MessageType = string;
/** The notification message payload */
export type Message = {
  type: MessageType;
  data: unknown;
};

/** The wrapped subscription message returned on the websocket connection */
export type PubSubMessage = {
  type: 'message';
  data: {
    message: Message;
    timestamp: string;
  };
};
export type Listener = (message: Message) => void;
export type Listeners = Map<string, Set<Listener>>;
export type Disposer = () => void;

/**
 * Used to subscribe to notification messages.
 * @example
 * ```
 * // Get access to subscriber instance.
 * const instance = NotificationSubscriber.getInstance();
 * // Subscribe to a particular type of notification.
 * const disposer = instance.subscribe('notification', (message: Message) => {});
 * // Unsubscribe when no-longer needing the subscription.
 * disposer();
 * ```
 */
class NotificationSubscriber {
  private static instance: NotificationSubscriber;

  public static getInstance(): NotificationSubscriber {
    if (!NotificationSubscriber.instance) {
      NotificationSubscriber.instance = new NotificationSubscriber();
    }
    return NotificationSubscriber.instance;
  }

  private channel: BroadcastChannel<Message> | null = null;

  private constructor() {
    if (typeof window !== 'undefined') {
      this.channel = new BroadcastChannel('messageProxy', { webWorkerSupport: false });
      this.channel.addEventListener('message', this.onMessage);
    }
  }

  private listeners: Listeners = new Map();

  private onMessage = (message: PubSubMessage) => {
    // Notify specific subscribers
    this.listeners.get(message.data.message.type)?.forEach((fn) => fn(message.data.message));
    // Notify global subscribers
    this.listeners.get('*')?.forEach((fn) => fn(message.data.message));
  };

  public subscribe(type: MessageType, fn: Listener): Disposer {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(fn);
    return () => {
      this.unsubscribe(type, fn);
    };
  }

  private unsubscribe(type: MessageType, fn: Listener): void {
    if (this.listeners.has(type)) {
      this.listeners.get(type).delete(fn);
      if (!this.listeners.get(type).size) {
        this.listeners.delete(type);
      }
    }
  }
}

const useNotificationSubscription = (type: MessageType, fn: Listener): void => {
  useEffect(
    () => {
      const disposer = NotificationSubscriber.getInstance().subscribe(type, fn);
      return disposer;
    },
    [fn, type],
  );
};

export default useNotificationSubscription;
